import React, { useState, useEffect, useRef } from 'react';
import { X, Filter, Search, Package } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import ProductGradeBadge from '@/components/ProductGradeBadge';

interface FilterSidebarProps {
  searchQuery: string;
  setSearchQuery: (value: string) => void;
  subCategory: string;
  setSubCategory: (value: string) => void;
  priceRange: string;
  setPriceRange: (value: string) => void;
  sortBy: string;
  setSortBy: (value: string) => void;
  selectedMaterial: string[];
  setSelectedMaterial: React.Dispatch<React.SetStateAction<string[]>>;
  selectedColorFamily: string[];
  setSelectedColorFamily: React.Dispatch<React.SetStateAction<string[]>>;
  selectedFinish: string[];
  setSelectedFinish: React.Dispatch<React.SetStateAction<string[]>>;
  selectedSaleType: string[];
  setSelectedSaleType: React.Dispatch<React.SetStateAction<string[]>>;
  selectedGrades: string[];
  setSelectedGrades: React.Dispatch<React.SetStateAction<string[]>>;
  subcategories: any[];
  availableFilters: {
    materials: string[];
    colorFamilies: string[];
    finishes: string[];
    saleTypes: string[];
    grades: string[];
  };
  categoryName?: string;
  resultsCount?: number;
  isLoading?: boolean;
  minQuantity: string;
  setMinQuantity: (value: string) => void;
}

const FilterPill = ({ label, options, value, onChange, activeCount, isMulti = false }: any) => {
  return (
    <div className="relative flex-shrink-0">
      <div className={`
        flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-base font-medium whitespace-nowrap transition-all duration-200
        ${activeCount > 0
          ? 'bg-primary text-primary-foreground border-primary shadow-sm'
          : 'bg-background border-border text-muted-foreground hover:border-primary/50 hover:text-foreground'}
      `}>
        <span>{label}</span>
        {activeCount > 0 && (
          <span className="flex items-center justify-center bg-white text-primary rounded-full w-4 h-4 text-[10px] font-bold">
            {activeCount}
          </span>
        )}
        <select
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          value={isMulti ? "" : (value || "")}
          onChange={(e) => {
            onChange(e.target.value);
            // Reset value for multi-select so the label stays visible
            if (isMulti) e.target.value = "";
          }}
        >
          <option value="" disabled>{label}</option>
          {options.map((opt: any) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
};

const FilterSidebar = ({
  searchQuery,
  setSearchQuery,
  subCategory,
  setSubCategory,
  priceRange,
  setPriceRange,
  sortBy,
  setSortBy,
  selectedMaterial,
  setSelectedMaterial,
  selectedColorFamily,
  setSelectedColorFamily,
  selectedFinish,
  setSelectedFinish,
  selectedSaleType,
  setSelectedSaleType,
  selectedGrades,
  setSelectedGrades,
  subcategories,
  availableFilters,
  categoryName,
  resultsCount = 0,
  isLoading = false,
  minQuantity,
  setMinQuantity,
}: FilterSidebarProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const [isQuantityExpanded, setIsQuantityExpanded] = useState(false);

  const saleTypeLabels: Record<string, string> = {
    'regular': 'רגילה',
    'immediate': 'רגילה',
    'timer': 'מכירה מוגבלת בזמן',
    'bid': 'מכרז',
    'liquidation': 'מכירת חיסול',
    'group_buy': 'קבוצת רכישה',
    'group': 'קבוצת רכישה'
  };

  const getSaleTypeLabel = (type: string) => saleTypeLabels[type] || type;

  const clearAllFilters = () => {
    setSearchQuery('');
    setSubCategory('');
    setPriceRange('');
    setMinQuantity('');
    setSelectedMaterial([]);
    setSelectedColorFamily([]);
    setSelectedFinish([]);
    setSelectedSaleType([]);
    setSelectedGrades([]);
    setSortBy('newest');
  };

  const toggleFilter = (filterType: 'material' | 'color' | 'finish' | 'saleType' | 'grade', value: string) => {
    switch (filterType) {
      case 'material':
        setSelectedMaterial(prev =>
          prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]
        );
        break;
      case 'color':
        setSelectedColorFamily(prev =>
          prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]
        );
        break;
      case 'finish':
        setSelectedFinish(prev =>
          prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]
        );
        break;
      case 'saleType':
        setSelectedSaleType(prev =>
          prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]
        );
        break;
      case 'grade':
        setSelectedGrades(prev =>
          prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]
        );
        break;
    }
  };

  const removeFilter = (filterType: 'material' | 'color' | 'finish' | 'saleType' | 'grade' | 'quantity' | 'price' | 'subCategory' | 'search', value: string) => {
    if (filterType === 'search') {
      setSearchQuery('');
      return;
    }
    if (filterType === 'quantity') {
      setMinQuantity('');
      return;
    }
    if (filterType === 'price') {
      setPriceRange('all');
      return;
    }
    if (filterType === 'subCategory') {
      setSubCategory('all');
      return;
    }
    toggleFilter(filterType as any, value);
  };

  const getActiveFiltersCount = () => {
    let count = selectedMaterial.length + selectedColorFamily.length + selectedFinish.length + selectedSaleType.length + selectedGrades.length;
    if (searchQuery && searchQuery.trim() !== '') {
      count += 1;
    }
    if (minQuantity && minQuantity !== '') {
      count += 1;
    }
    if (priceRange && priceRange !== 'all') {
      count += 1;
    }
    if (subCategory && subCategory !== 'all' && subCategory !== '') {
      count += 1;
    }
    return count;
  };

  const renderActiveBadges = () => (
    <div className="flex flex-wrap gap-2">
      {searchQuery && searchQuery.trim() !== '' && (
        <Badge variant="secondary" className="text-xs bg-muted text-foreground border-none hover:bg-muted/80">
          חיפוש: {searchQuery}
          <X
            className="h-3 w-3 ml-1 cursor-pointer"
            onClick={() => removeFilter('search', searchQuery)}
          />
        </Badge>
      )}
      {minQuantity && minQuantity !== '' && (
        <Badge variant="secondary" className="text-xs bg-muted text-foreground border-none hover:bg-muted/80">
          כמות: {minQuantity}
          <X
            className="h-3 w-3 ml-1 cursor-pointer"
            onClick={() => removeFilter('quantity', minQuantity)}
          />
        </Badge>
      )}
      {priceRange && priceRange !== 'all' && (
        <Badge variant="secondary" className="text-xs bg-muted text-foreground border-none hover:bg-muted/80">
          מחיר: {priceRange === '200+' ? '₪200+' : `₪${priceRange}`}
          <X
            className="h-3 w-3 ml-1 cursor-pointer"
            onClick={() => removeFilter('price', priceRange)}
          />
        </Badge>
      )}
      {subCategory && subCategory !== 'all' && subCategory !== '' && (
        <Badge variant="secondary" className="text-xs bg-muted text-foreground border-none hover:bg-muted/80">
          {subCategory}
          <X
            className="h-3 w-3 ml-1 cursor-pointer"
            onClick={() => removeFilter('subCategory', subCategory)}
          />
        </Badge>
      )}
      {selectedGrades.map(grade => (
        <Badge key={grade} variant="secondary" className="text-xs bg-muted text-foreground border-none hover:bg-muted/80">
          דירוג {grade}
          <X
            className="h-3 w-3 ml-1 cursor-pointer"
            onClick={() => removeFilter('grade', grade)}
          />
        </Badge>
      ))}
      {selectedSaleType.map(type => (
        <Badge key={type} variant="secondary" className="text-xs bg-muted text-foreground border-none hover:bg-muted/80">
          {getSaleTypeLabel(type)}
          <X
            className="h-3 w-3 ml-1 cursor-pointer"
            onClick={() => removeFilter('saleType', type)}
          />
        </Badge>
      ))}
      {selectedMaterial.map(material => (
        <Badge key={material} variant="secondary" className="text-xs bg-muted text-foreground border-none hover:bg-muted/80">
          {material}
          <X
            className="h-3 w-3 ml-1 cursor-pointer"
            onClick={() => removeFilter('material', material)}
          />
        </Badge>
      ))}
      {selectedColorFamily.map(color => (
        <Badge key={color} variant="secondary" className="text-xs bg-muted text-foreground border-none hover:bg-muted/80">
          {color}
          <X
            className="h-3 w-3 ml-1 cursor-pointer"
            onClick={() => removeFilter('color', color)}
          />
        </Badge>
      ))}
      {selectedFinish.map(finish => (
        <Badge key={finish} variant="secondary" className="text-xs bg-muted text-foreground border-none hover:bg-muted/80">
          {finish}
          <X
            className="h-3 w-3 ml-1 cursor-pointer"
            onClick={() => removeFilter('finish', finish)}
          />
        </Badge>
      ))}
    </div>
  );

  const renderFiltersContent = () => (
    <div className="space-y-6">
      {/* Active Filters */}
      {getActiveFiltersCount() > 0 && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium">סינונים פעילים</label>
            <Button
              variant="ghost"
              size="sm"
              onClick={clearAllFilters}
              className="text-xs text-muted-foreground hover:text-foreground"
            >
              נקה הכל
            </Button>
          </div>
          {renderActiveBadges()}
        </div>
      )}

      {/* Search */}
      <div className="space-y-2">
        <label className="text-sm font-medium">חיפוש</label>
        <Input
          placeholder={`חפש ב${categoryName || 'קטגוריה'}...`}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="max-w-[calc(100vw-3rem)] lg:max-w-none"
        />
      </div>

      {/* Sub-category Filter */}
      {subcategories && subcategories.length > 0 && (
        <div className="space-y-2">
          <label className="text-sm font-medium">תת-קטגוריה</label>
          <select
            value={subCategory}
            onChange={(e) => setSubCategory(e.target.value)}
            className="w-full p-3 border border-input rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary hover:border-muted-foreground transition-colors duration-200 appearance-none pr-10 relative max-w-[calc(100vw-3rem)] lg:max-w-none"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
              backgroundPosition: 'right 0.75rem center',
              backgroundRepeat: 'no-repeat',
              backgroundSize: '1.5em 1.5em'
            }}
          >
            <option value="" disabled>בחר תת-קטגוריה</option>
            <option value="all">כל התת-קטגוריות</option>
            {subcategories.map(sub => (
              sub && sub.id && sub.name ? (
                <option key={sub.id} value={sub.name}>{sub.name}</option>
              ) : null
            ))}
          </select>
        </div>
      )}

      {/* Price Range */}
      <div className="space-y-2">
        <label className="text-sm font-medium">טווח מחירים</label>
        <select
          value={priceRange}
          onChange={(e) => setPriceRange(e.target.value)}
          className="w-full p-3 border border-input rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary hover:border-muted-foreground transition-colors duration-200 appearance-none pr-10 max-w-[calc(100vw-3rem)] lg:max-w-none"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
            backgroundPosition: 'right 0.75rem center',
            backgroundRepeat: 'no-repeat',
            backgroundSize: '1.5em 1.5em'
          }}
        >
          <option value="" disabled>בחר טווח מחירים</option>
          <option value="all">כל המחירים</option>
          <option value="0-50">₪0 - ₪50</option>
          <option value="50-100">₪50 - ₪100</option>
          <option value="100-200">₪100 - ₪200</option>
          <option value="200+">₪200+</option>
        </select>
      </div>

      {/* 4. Product Grade Filter - NEW */}
      {availableFilters.grades && availableFilters.grades.length > 0 && (
        <div className="space-y-2">
          <label className="text-sm font-medium">דירוג מוצר</label>
          <div className="space-y-2">
            {/* Sort grades to ensure A comes before B, etc. */}
            {availableFilters.grades.sort().map((grade) => (
              <div key={grade} className="flex items-center gap-3">
                <Checkbox
                  id={`grade-${grade}`}
                  checked={selectedGrades.includes(grade)}
                  onCheckedChange={() => toggleFilter('grade', grade)}
                />
                <label
                  htmlFor={`grade-${grade}`}
                  className="text-sm font-normal cursor-pointer flex-1 flex items-center gap-2"
                >
                  <ProductGradeBadge grade={grade as any} size="sm" />
                  <span className="text-muted-foreground text-xs">
                    {grade === 'A' ? 'פרימיום' :
                      grade === 'B' ? 'סטנדרט' :
                        grade === 'C' ? 'כפי שהוא' : 'מכירה סופית'}
                  </span>
                </label>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 5. Sale Type Filter */}
      {availableFilters.saleTypes && availableFilters.saleTypes.length > 0 && (
        <div className="space-y-2">
          <label className="text-sm font-medium">סוג מכירה</label>
          <div className="space-y-2 max-h-32 overflow-y-auto">
            {availableFilters.saleTypes.map((type) => (
              <div key={type} className="flex items-center gap-3">
                <Checkbox
                  id={`saleType-${type}`}
                  checked={selectedSaleType.includes(type)}
                  onCheckedChange={() => toggleFilter('saleType', type)}
                />
                <label
                  htmlFor={`saleType-${type}`}
                  className="text-sm font-normal cursor-pointer flex-1"
                >
                  {getSaleTypeLabel(type)}
                </label>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 6. Minimum Quantity Filter */}
      <div className="space-y-2">
        <label className="text-sm font-medium">כמות מבוקשת</label>
        <Input
          type="number"
          min="0"
          placeholder='כמות מינימלית (מ"ר/יח׳)'
          value={minQuantity}
          onChange={(e) => setMinQuantity(e.target.value)}
          className="w-full max-w-[calc(100vw-3rem)] lg:max-w-none"
        />
        <p className="text-xs text-muted-foreground">
          הצג רק מוצרים עם מלאי מספיק
        </p>
      </div>

      {/* 7. Material Filter */}
      {availableFilters.materials.length > 0 && (
        <div className="space-y-2">
          <label className="text-sm font-medium">חומר</label>
          <div className="space-y-2 max-h-32 overflow-y-auto">
            {availableFilters.materials.map((material) => (
              <div key={material} className="flex items-center gap-3">
                <Checkbox
                  id={`material-${material}`}
                  checked={selectedMaterial.includes(material)}
                  onCheckedChange={() => toggleFilter('material', material)}
                />
                <label
                  htmlFor={`material-${material}`}
                  className="text-sm font-normal cursor-pointer flex-1"
                >
                  {material}
                </label>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 8. Color Family Filter */}
      {availableFilters.colorFamilies.length > 0 && (
        <div className="space-y-2">
          <label className="text-sm font-medium">צבע</label>
          <div className="space-y-2 max-h-32 overflow-y-auto">
            {availableFilters.colorFamilies.map((color) => (
              <div key={color} className="flex items-center gap-3">
                <Checkbox
                  id={`color-${color}`}
                  checked={selectedColorFamily.includes(color)}
                  onCheckedChange={() => toggleFilter('color', color)}
                />
                <label
                  htmlFor={`color-${color}`}
                  className="text-sm font-normal cursor-pointer flex-1"
                >
                  {color}
                </label>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 9. Finish Filter */}
      {availableFilters.finishes.length > 0 && (
        <div className="space-y-2">
          <label className="text-sm font-medium">גימור</label>
          <div className="space-y-2 max-h-32 overflow-y-auto">
            {availableFilters.finishes.map((finish) => (
              <div key={finish} className="flex items-center gap-3">
                <Checkbox
                  id={`finish-${finish}`}
                  checked={selectedFinish.includes(finish)}
                  onCheckedChange={() => toggleFilter('finish', finish)}
                />
                <label
                  htmlFor={`finish-${finish}`}
                  className="text-sm font-normal cursor-pointer flex-1"
                >
                  {finish}
                </label>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 10. Sort (moved to end as requested hierarchically, though usually sort is separate. User asked for this order in sidebar) */}
      <div className="space-y-2">
        <label className="text-sm font-medium">מיין לפי</label>
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          className="w-full p-3 border border-input rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary hover:border-muted-foreground transition-colors duration-200 appearance-none pr-10 max-w-[calc(100vw-3rem)] lg:max-w-none"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
            backgroundPosition: 'right 0.75rem center',
            backgroundRepeat: 'no-repeat',
            backgroundSize: '1.5em 1.5em'
          }}
        >
          <option value="" disabled>מיין לפי</option>
          <option value="newest">החדשים ביותר</option>
          <option value="price-low">מחיר נמוך לגבוה</option>
          <option value="price-high">מחיר גבוה לנמוך</option>
        </select>
      </div>

      {/* Clear Filters */}
      {getActiveFiltersCount() > 0 && (
        <Button
          variant="outline"
          onClick={clearAllFilters}
          className="w-full"
        >
          נקה כל הסינונים ({getActiveFiltersCount()})
        </Button>
      )}
    </div>
  );

  return (
    <>
      {/* Mobile Filter Slider */}
      <div className="lg:hidden mb-6">
        <div className="flex items-center justify-between mb-3 px-1">
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-primary" />
            <div className="flex items-center gap-1.5">
              <span className="text-sm font-bold">מסננים:</span>
              {getActiveFiltersCount() > 0 && (
                <span className="flex items-center justify-center bg-primary text-white rounded-full min-w-[18px] h-[18px] px-1 text-[10px] font-bold">
                  {getActiveFiltersCount()}
                </span>
              )}
            </div>
          </div>
          {getActiveFiltersCount() >= 2 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={clearAllFilters}
              className="text-xs h-7 px-2 text-primary hover:text-primary/80"
            >
              נקה הכל
            </Button>
          )}
        </div>

        <div className="relative -mx-4 pt-1">
          {!isSearchExpanded && !isQuantityExpanded && (
            <>
              <div className="absolute inset-y-0 left-0 w-8 bg-gradient-to-r from-background to-transparent pointer-events-none z-10" />
              <div className="absolute inset-y-0 right-0 w-8 bg-gradient-to-l from-background to-transparent pointer-events-none z-10" />
            </>
          )}
          <div className="flex overflow-x-auto gap-2 py-1.5 pb-2 scrollbar-hide px-4">
            {/* Search Pill/Input with Animation */}
            <div
              className={`relative transition-all duration-300 ease-in-out flex-shrink-0 ${isSearchExpanded ? 'flex-1 min-w-[200px]' : 'w-10'} ${isQuantityExpanded && !isSearchExpanded ? 'hidden' : 'block'}`}
            >
              <div
                onClick={() => setIsSearchExpanded(true)}
                className={`flex items-center justify-center rounded-full border h-10 w-10 cursor-pointer transition-all duration-300 ${searchQuery ? 'bg-primary border-primary text-white' : 'bg-background border-border text-muted-foreground'} ${isSearchExpanded ? 'opacity-0 scale-90 pointer-events-none absolute' : 'opacity-100 scale-100'}`}
              >
                <Search className="h-5 w-5" />
              </div>

              <div className={`relative w-full transition-all duration-300 ${isSearchExpanded ? 'opacity-100 scale-100' : 'opacity-0 scale-95 pointer-events-none absolute inset-0'}`}>
                <Input
                  autoFocus={isSearchExpanded}
                  placeholder="חפש מוצרים..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && setIsSearchExpanded(false)}
                  onBlur={() => setIsSearchExpanded(false)}
                  className="rounded-full border-2 border-[#8dcf2b] focus-visible:ring-0 focus:ring-0 ring-offset-0 focus-visible:ring-offset-0 bg-background h-10 pr-10 pl-10 w-full text-base transition-all shadow-none"
                />
                <Search className="absolute right-3.5 top-1/2 -translate-y-1/2 h-5 w-5 text-[#8dcf2b]" />
                {searchQuery && (
                  <button
                    onMouseDown={(e) => {
                      e.preventDefault();
                      setSearchQuery('');
                    }}
                    className="absolute left-3 top-1/2 -translate-y-1/2 p-1 rounded-full hover:bg-muted transition-colors"
                  >
                    <X className="h-4 w-4 text-muted-foreground" />
                  </button>
                )}
              </div>
            </div>

            {/* Quantity Pill/Input with Animation */}
            <div
              className={`relative transition-all duration-300 ease-in-out flex-shrink-0 ${isQuantityExpanded ? 'flex-1 min-w-[200px]' : 'w-[85px]'} ${isSearchExpanded && !isQuantityExpanded ? 'hidden' : 'block'}`}
            >
              <div
                onClick={() => setIsQuantityExpanded(true)}
                className={`flex items-center justify-center gap-1.5 px-3 rounded-full border h-10 cursor-pointer transition-all duration-300 text-base font-medium whitespace-nowrap ${minQuantity ? 'bg-primary border-primary text-white' : 'bg-background border-[#8dcf2b] text-muted-foreground'} ${isQuantityExpanded ? 'opacity-0 scale-90 pointer-events-none absolute' : 'opacity-100 scale-100'}`}
              >
                <span>כמות</span>
                {minQuantity && (
                  <span className="flex items-center justify-center bg-white text-primary rounded-full w-4 h-4 text-[10px] font-bold">
                    1
                  </span>
                )}
              </div>

              <div className={`relative w-full transition-all duration-300 ${isQuantityExpanded ? 'opacity-100 scale-100' : 'opacity-0 scale-95 pointer-events-none absolute inset-0'}`}>
                <Input
                  type="number"
                  autoFocus={isQuantityExpanded}
                  placeholder="כמות מינימלית"
                  value={minQuantity}
                  onChange={(e) => setMinQuantity(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && setIsQuantityExpanded(false)}
                  onBlur={() => setIsQuantityExpanded(false)}
                  className="rounded-full border-2 border-[#8dcf2b] focus-visible:ring-0 focus:ring-0 ring-offset-0 focus-visible:ring-offset-0 bg-background h-10 pl-4 pr-10 w-full text-base text-right transition-all shadow-none"
                  dir="rtl"
                />
                <Package className="absolute right-3.5 top-1/2 -translate-y-1/2 h-5 w-5 text-[#8dcf2b]" />
              </div>
            </div>

            {!isSearchExpanded && !isQuantityExpanded && (
              <>
                {/* Sub-category */}
                {subcategories && subcategories.length > 0 && (
                  <FilterPill
                    label="תת-קטגוריה"
                    activeCount={(subCategory && subCategory !== 'all') ? 1 : 0}
                    value={subCategory}
                    onChange={(val: string) => setSubCategory(val)}
                    options={[
                      { value: 'all', label: 'כל התת-קטגוריות' },
                      ...subcategories.map(sub => ({ value: sub.name, label: sub.name }))
                    ]}
                  />
                )}

                {/* Price */}
                <FilterPill
                  label="מחיר"
                  activeCount={(priceRange && priceRange !== 'all') ? 1 : 0}
                  value={priceRange}
                  onChange={(val: string) => setPriceRange(val)}
                  options={[
                    { value: 'all', label: 'כל המחירים' },
                    { value: '0-50', label: '₪0 - ₪50' },
                    { value: '50-100', label: '₪50 - ₪100' },
                    { value: '100-200', label: '₪100 - ₪200' },
                    { value: '200+', label: '₪200+' }
                  ]}
                />

                {/* Grade */}
                {availableFilters.grades && availableFilters.grades.length > 0 && (
                  <FilterPill
                    label="דירוג"
                    activeCount={selectedGrades.length}
                    value=""
                    isMulti
                    onChange={(val: string) => toggleFilter('grade', val)}
                    options={availableFilters.grades.sort().map(grade => ({
                      value: grade,
                      label: `דירוג ${grade}: ${grade === 'A' ? 'פרימיום' :
                        grade === 'B' ? 'סטנדרט' :
                          grade === 'C' ? 'כפי שהוא' : 'מכירה סופית'
                        }`
                    }))}
                  />
                )}

                {/* Sale Type */}
                {availableFilters.saleTypes && availableFilters.saleTypes.length > 0 && (
                  <FilterPill
                    label="סוג מכירה"
                    activeCount={selectedSaleType.length}
                    value=""
                    isMulti
                    onChange={(val: string) => toggleFilter('saleType', val)}
                    options={availableFilters.saleTypes.map(type => ({
                      value: type,
                      label: getSaleTypeLabel(type)
                    }))}
                  />
                )}

                {/* Material */}
                {availableFilters.materials.length > 0 && (
                  <FilterPill
                    label="חומר"
                    activeCount={selectedMaterial.length}
                    value=""
                    isMulti
                    onChange={(val: string) => toggleFilter('material', val)}
                    options={availableFilters.materials.map(m => ({ value: m, label: m }))}
                  />
                )}

                {/* Color */}
                {availableFilters.colorFamilies.length > 0 && (
                  <FilterPill
                    label="צבע"
                    activeCount={selectedColorFamily.length}
                    value=""
                    isMulti
                    onChange={(val: string) => toggleFilter('color', val)}
                    options={availableFilters.colorFamilies.map(c => ({ value: c, label: c }))}
                  />
                )}

                {/* Finish */}
                {availableFilters.finishes.length > 0 && (
                  <FilterPill
                    label="גימור"
                    activeCount={selectedFinish.length}
                    value=""
                    isMulti
                    onChange={(val: string) => toggleFilter('finish', val)}
                    options={availableFilters.finishes.map(f => ({ value: f, label: f }))}
                  />
                )}

                {/* Sort */}
                <FilterPill
                  label="מיון"
                  activeCount={sortBy && sortBy !== 'newest' ? 1 : 0}
                  value={sortBy}
                  onChange={(val: string) => setSortBy(val)}
                  options={[
                    { value: 'newest', label: 'החדשים ביותר' },
                    { value: 'price-low', label: 'מחיר נמוך לגבוה' },
                    { value: 'price-high', label: 'מחיר גבוה לנמוך' }
                  ]}
                />
              </>
            )}
          </div>

          {/* Active Badges */}
          {getActiveFiltersCount() > 0 && (
            <div className="mt-1 pt-2 border-t border-border/10">
              {renderActiveBadges()}
            </div>
          )}
        </div>
      </div>

      {/* Desktop Filter Bar */}
      <div className="hidden lg:block bg-card rounded-xl border p-4 mb-2">
        <div className="flex flex-wrap items-center gap-3">
          {/* Desktop Search */}
          <div className="relative w-64">
            <Input
              placeholder={`חפש ב${categoryName || 'קטגוריה'}...`}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="rounded-full border-border bg-background h-10 pr-10 pl-4 w-full text-sm"
            />
            <Search className="absolute right-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          </div>

          {/* Quantity */}
          <div className="relative w-40">
            <Input
              type="number"
              placeholder="כמות (מ״ר/יח׳)"
              value={minQuantity}
              onChange={(e) => setMinQuantity(e.target.value)}
              className="rounded-full border-border bg-background h-10 pr-10 pl-4 w-full text-sm"
              dir="rtl"
            />
            <Package className="absolute right-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          </div>

          {/* Sub-category */}
          {subcategories && subcategories.length > 0 && (
            <FilterPill
              label="תת-קטגוריה"
              activeCount={(subCategory && subCategory !== 'all') ? 1 : 0}
              value={subCategory}
              onChange={(val: string) => setSubCategory(val)}
              options={subcategories.filter(s => s && s.id && s.name).map(s => ({ value: s.name, label: s.name }))}
            />
          )}

          {/* Price */}
          <FilterPill
            label="טווח מחירים"
            activeCount={(priceRange && priceRange !== 'all') ? 1 : 0}
            value={priceRange}
            onChange={(val: string) => setPriceRange(val)}
            options={[
              { value: 'all', label: 'כל המחירים' },
              { value: '0-50', label: '₪0 - ₪50' },
              { value: '50-100', label: '₪50 - ₪100' },
              { value: '100-200', label: '₪100 - ₪200' },
              { value: '200+', label: '₪200+' }
            ]}
          />

          {/* Grade */}
          {availableFilters.grades && availableFilters.grades.length > 0 && (
            <FilterPill
              label="דירוג"
              activeCount={selectedGrades.length}
              value=""
              isMulti
              onChange={(val: string) => toggleFilter('grade', val)}
              options={availableFilters.grades.sort().map(g => ({ value: g, label: `דירוג ${g}` }))}
            />
          )}

          {/* Sale Type */}
          {availableFilters.saleTypes && availableFilters.saleTypes.length > 0 && (
            <FilterPill
              label="סוג מכירה"
              activeCount={selectedSaleType.length}
              value=""
              isMulti
              onChange={(val: string) => toggleFilter('saleType', val)}
              options={availableFilters.saleTypes.map(t => ({ value: t, label: getSaleTypeLabel(t) }))}
            />
          )}

          {/* Material */}
          {availableFilters.materials.length > 0 && (
            <FilterPill
              label="חומר"
              activeCount={selectedMaterial.length}
              value=""
              isMulti
              onChange={(val: string) => toggleFilter('material', val)}
              options={availableFilters.materials.map(m => ({ value: m, label: m }))}
            />
          )}

          {/* Color */}
          {availableFilters.colorFamilies.length > 0 && (
            <FilterPill
              label="צבע"
              activeCount={selectedColorFamily.length}
              value=""
              isMulti
              onChange={(val: string) => toggleFilter('color', val)}
              options={availableFilters.colorFamilies.map(c => ({ value: c, label: c }))}
            />
          )}

          {/* Finish */}
          {availableFilters.finishes.length > 0 && (
            <FilterPill
              label="גימור"
              activeCount={selectedFinish.length}
              value=""
              isMulti
              onChange={(val: string) => toggleFilter('finish', val)}
              options={availableFilters.finishes.map(f => ({ value: f, label: f }))}
            />
          )}

          {/* Sort */}
          <FilterPill
            label="מיון"
            activeCount={sortBy && sortBy !== 'newest' ? 1 : 0}
            value={sortBy}
            onChange={(val: string) => setSortBy(val)}
            options={[
              { value: 'newest', label: 'החדשים ביותר' },
              { value: 'price-low', label: 'מחיר נמוך לגבוה' },
              { value: 'price-high', label: 'מחיר גבוה לנמוך' }
            ]}
          />

          {/* Clear Filters Button (Desktop) */}
          {getActiveFiltersCount() > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={clearAllFilters}
              className="text-xs text-primary hover:text-primary/80 h-10 px-4 rounded-full border border-dashed border-primary/30"
            >
              נקה הכל ({getActiveFiltersCount()})
            </Button>
          )}
        </div>

        {/* Desktop Active Badges */}
        {getActiveFiltersCount() > 0 && (
          <div className="mt-4 pt-4 border-t border-border/50">
            {renderActiveBadges()}
          </div>
        )}
      </div>
    </>
  );
};

export default FilterSidebar;